<div class="platform-charge-transactions-container">
  <!-- Header -->
  <div class="header">
    <div class="header-content">
      <div>
        <h1>Platform Charge Transactions</h1>
        <p>View and manage platform charge transactions</p>
      </div>
      <div class="header-actions">
        <button 
          class="export-btn" 
          (click)="exportToCSV()"
          [disabled]="loading || transactions.length === 0"
          title="Export current page data to CSV">
          <i class="fas fa-download"></i> Export to CSV
        </button>
      </div>
    </div>
  </div>

  <!-- Filters -->
  <div class="filters-section">
    <div class="filters-grid">
      <div class="filter-group">
        <label for="agentFilter">Agent:</label>
        <select 
          id="agentFilter" 
          [(ngModel)]="selectedAgentId" 
          (change)="onFilterChange()"
          class="filter-select">
          <option value="">All Agents</option>
          <option *ngFor="let agent of agents" [value]="agent.agentId">
            {{ agent.name }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label for="zoneFilter">Zone:</label>
        <select 
          id="zoneFilter" 
          [(ngModel)]="selectedZoneId" 
          (change)="onFilterChange()"
          class="filter-select">
          <option value="">All Zones</option>
          <option *ngFor="let zone of zones" [value]="zone.id">
            {{ zone.name }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label for="remittanceFilter">Remittance Status:</label>
        <select 
          id="remittanceFilter" 
          [(ngModel)]="selectedHasRemittance" 
          (change)="onFilterChange()"
          class="filter-select">
          <option value="">All</option>
          <option value="true">Has Remittance</option>
          <option value="false">No Remittance</option>
        </select>
      </div>

      <div class="filter-group">
        <label for="fromDate">From Date:</label>
        <input 
          type="date" 
          id="fromDate" 
          [(ngModel)]="fromDate" 
          (change)="onFilterChange()"
          class="filter-select">
      </div>

      <div class="filter-group">
        <label for="toDate">To Date:</label>
        <input 
          type="date" 
          id="toDate" 
          [(ngModel)]="toDate" 
          (change)="onFilterChange()"
          class="filter-select">
      </div>

      <div class="filter-actions">
        <button 
          class="clear-filters-btn" 
          (click)="clearFilters()"
          [disabled]="loading">
          <i class="fas fa-times"></i> Clear Filters
        </button>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage" class="error-message">
    <i class="fas fa-exclamation-circle mr-2"></i>
    {{ errorMessage }}
  </div>

  <!-- Success Message -->
  <div *ngIf="successMessage" class="success-message">
    <i class="fas fa-check-circle mr-2"></i>
    {{ successMessage }}
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <div class="loading-spinner"></div>
    <span class="loading-text">Loading platform charge transactions...</span>
  </div>

  <!-- Results Summary -->
  <div *ngIf="!loading && transactions.length > 0" class="results-summary">
    <p>Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} transactions</p>
    <p class="total-amount-display">Total Amount: <strong>{{ formatCurrency(totalAmount) }}</strong></p>
  </div>

  <!-- Bulk Remittance Actions -->
  <div *ngIf="!loading && transactions.length > 0" class="bulk-actions-section">
    <div class="bulk-actions-header">
      <div class="selection-info">
        <span *ngIf="selectedTransactionsCount > 0" class="selection-count">
          {{ selectedTransactionsCount }} transaction(s) selected
          <span *ngIf="selectedTransactionsCount > 0" class="selected-amount">
            (Total: {{ formatCurrency(selectedTransactionsTotalAmount) }})
          </span>
        </span>
        <span *ngIf="selectedTransactionsCount === 0" class="no-selection">
          Select transactions to update remittance
        </span>
      </div>
      <div class="bulk-actions-buttons">
        <button 
          class="select-all-btn" 
          (click)="selectAllTransactions()"
          [disabled]="loading || selectedTransactionsCount === transactions.length">
          <i class="fas fa-check-square"></i> Select All
        </button>
        <button 
          class="clear-selection-btn" 
          (click)="clearSelection()"
          [disabled]="loading || selectedTransactionsCount === 0">
          <i class="fas fa-times"></i> Clear Selection
        </button>
        <button 
          class="bulk-remittance-btn" 
          (click)="openBulkRemittanceForm()"
          [disabled]="loading || selectedTransactionsCount === 0">
          <i class="fas fa-money-bill-wave"></i> Update Remittance
        </button>
      </div>
    </div>
  </div>

  <!-- Desktop Table View -->
  <div *ngIf="!loading && transactions.length > 0" class="desktop-table-container">
    <div class="table-wrapper">
      <table class="transactions-table">
        <thead>
          <tr>
            <th class="checkbox-column">
              <input 
                type="checkbox" 
                [checked]="selectedTransactionsCount === transactions.length && transactions.length > 0"
                [indeterminate]="selectedTransactionsCount > 0 && selectedTransactionsCount < transactions.length"
                (change)="selectedTransactionsCount === transactions.length ? clearSelection() : selectAllTransactions()"
                class="select-all-checkbox">
            </th>
            <th>Agent Name</th>
            <th>Platform Charge %</th>
            <th>Platform Charge Amount</th>
            <th>CGST</th>
            <th>SGST</th>
            <th>Collection Date</th>
            <th>Remittance Date</th>
            <th>Remittance Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let transaction of transactions" class="transaction-row" 
              [class.selected]="isTransactionSelected(transaction.id)">
            <td class="checkbox-column">
              <input 
                type="checkbox" 
                [checked]="isTransactionSelected(transaction.id)"
                (change)="toggleTransactionSelection(transaction.id)"
                class="transaction-checkbox">
            </td>
            <td class="agent-name">{{ transaction.agentName }}</td>
            <td class="percentage">{{ formatPercentage(transaction.platformChargeInPercentage) }}</td>
            <td class="amount">{{ formatCurrency(transaction.platformChargeAmount) }}</td>
            <td class="cgst">{{ formatCurrency(transaction.cgst) }}</td>
            <td class="sgst">{{ formatCurrency(transaction.sgst) }}</td>
            <td class="collection-date">{{ formatDate(transaction.collectionDate) }}</td>
            <td class="remittance-date">{{ formatDate(transaction.remittanceDate) }}</td>
            <td class="remittance-notes">{{ transaction.remittanceNotes || 'N/A' }}</td>
          </tr>
          <!-- Page Totals Row -->
          <tr class="transaction-row totals-row page-totals">
            <td class="checkbox-column"></td>
            <td class="agent-name"><strong>Page Totals</strong></td>
            <td class="percentage"></td>
            <td class="amount"><strong>{{ formatCurrency(totalPlatformChargeAmount) }}</strong></td>
            <td class="cgst"><strong>{{ formatCurrency(totalCgst) }}</strong></td>
            <td class="sgst"><strong>{{ formatCurrency(totalSgst) }}</strong></td>
            <td class="collection-date"></td>
            <td class="remittance-date"></td>
            <td class="remittance-notes"></td>
          </tr>
          <!-- Overall Total Row -->
          <tr class="transaction-row totals-row overall-totals">
            <td class="checkbox-column"></td>
            <td class="agent-name"><strong>Overall Total</strong></td>
            <td class="percentage"></td>
            <td class="amount"><strong>{{ formatCurrency(totalAmount) }}</strong></td>
            <td class="cgst"></td>
            <td class="sgst"></td>
            <td class="collection-date"></td>
            <td class="remittance-date"></td>
            <td class="remittance-notes"></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Mobile Card View -->
  <div *ngIf="!loading && transactions.length > 0" class="mobile-cards-container">
    <div *ngFor="let transaction of transactions" class="transaction-card"
         [class.selected]="isTransactionSelected(transaction.id)">
      <div class="card-header">
        <div class="card-header-left">
          <input 
            type="checkbox" 
            [checked]="isTransactionSelected(transaction.id)"
            (change)="toggleTransactionSelection(transaction.id)"
            class="mobile-checkbox">
          <h3>{{ transaction.agentName }}</h3>
        </div>
        <span class="collection-date">{{ formatDate(transaction.collectionDate) }}</span>
      </div>
      
      <div class="card-content">
        <div class="info-row">
          <span class="label">Platform Charge:</span>
          <span class="value">{{ formatPercentage(transaction.platformChargeInPercentage) }} - {{ formatCurrency(transaction.platformChargeAmount) }}</span>
        </div>
        
        <div class="info-row">
          <span class="label">CGST:</span>
          <span class="value">{{ formatCurrency(transaction.cgst) }}</span>
        </div>
        
        <div class="info-row">
          <span class="label">SGST:</span>
          <span class="value">{{ formatCurrency(transaction.sgst) }}</span>
        </div>
        
        <div class="info-row" *ngIf="transaction.remittanceDate">
          <span class="label">Remittance Date:</span>
          <span class="value">{{ formatDate(transaction.remittanceDate) }}</span>
        </div>
        
        <div class="info-row" *ngIf="transaction.remittanceNotes">
          <span class="label">Remittance Notes:</span>
          <span class="value">{{ transaction.remittanceNotes }}</span>
        </div>
      </div>
    </div>
    
    <!-- Mobile Total Amount Display -->
    <div class="mobile-total-amount">
      <div class="total-amount-card">
        <h3>Total Amount</h3>
        <p class="total-amount-value">{{ formatCurrency(totalAmount) }}</p>
      </div>
    </div>
  </div>

  <!-- No Data State -->
  <div *ngIf="!loading && transactions.length === 0" class="no-data-state">
    <div class="no-data-content">
      <i class="fas fa-receipt"></i>
      <h3>No Platform Charge Transactions Found</h3>
      <p>No transactions match your current filters.</p>
    </div>
  </div>

  <!-- Pagination -->
  <div *ngIf="!loading && transactions.length > 0" class="pagination-container">
    <app-pagination
      [page]="currentPage"
      [totalPages]="totalPages"
      [pageSize]="pageSize"
      (pageChange)="onPageChange($event)"
      (pageSizeChange)="onPageSizeChange($event)">
    </app-pagination>
  </div>

  <!-- Bulk Remittance Form Modal -->
  <div *ngIf="showBulkRemittanceForm" class="modal-overlay">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2>Update Remittance</h2>
        <button class="close-btn" (click)="closeBulkRemittanceForm()">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="form-info">
          <p><strong>{{ selectedTransactionsCount }}</strong> transaction(s) selected</p>
          <p>Total Amount: <strong>{{ formatCurrency(selectedTransactionsTotalAmount) }}</strong></p>
        </div>
        
        <form class="bulk-remittance-form" (ngSubmit)="submitBulkRemittance()">
          <div class="form-group">
            <label for="remittanceDate">Remittance Date *</label>
            <input 
              type="date" 
              id="remittanceDate"
              [(ngModel)]="bulkRemittanceForm.remittanceDate"
              name="remittanceDate"
              class="form-control"
              required>
          </div>
          
          <div class="form-group">
            <label for="remittanceMedium">Remittance Medium *</label>
            <input 
              type="text" 
              id="remittanceMedium"
              [(ngModel)]="bulkRemittanceForm.remittanceMedium"
              name="remittanceMedium"
              class="form-control"
              placeholder="e.g., UPI, Bank Transfer, Cash"
              required>
          </div>
          
          <div class="form-group">
            <label for="remittanceNotes">Remittance Notes *</label>
            <textarea 
              id="remittanceNotes"
              [(ngModel)]="bulkRemittanceForm.remittanceNotes"
              name="remittanceNotes"
              class="form-control"
              placeholder="Enter remittance details..."
              rows="3"
              required></textarea>
          </div>
          
          <div class="form-actions">
            <button 
              type="button" 
              class="cancel-btn"
              (click)="closeBulkRemittanceForm()"
              [disabled]="bulkRemittanceLoading">
              Cancel
            </button>
            <button 
              type="submit" 
              class="submit-btn"
              [disabled]="bulkRemittanceLoading">
              <span *ngIf="bulkRemittanceLoading" class="loading-spinner-small"></span>
              {{ bulkRemittanceLoading ? 'Updating...' : 'Update Remittance' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</div>
